// Share registry — the live record of each control's current value, written
// by the input islands and read by the Copy-share-link button (#244).
//
// Each island already pushes its committed value to Shiny via setInputValue;
// alongside that it calls recordControl() here with its inputId, value, and
// whether it differs from the default (the islands already compute this for
// their reset affordance). The ShareButton subscribes to this store, so it
// enables/updates the moment any control becomes modified — no polling.
//
// Keyed by inputId (globally unique, derived from stable spec ids), which is
// also the control key in the share token. encode reads the modified subset;
// the token codec lives in lib/share_token.ts.

import { create } from "zustand";
import { encodeShareToken } from "../lib/share_token";
import { appVersion, engineVersion, publicShareBase } from "../lib/app_meta";

/**
 * Static, display-only metadata an island knows at mount time (label, baseline,
 * how to format). It rides alongside the live value so the "Your reform" modal
 * (#65) can render each changed control's label, value, and delta-from-baseline
 * as a pure selector over the store — no DOM scraping. A discriminated union on
 * `kind`; `selectReformRows` (state/reform_summary.ts) dispatches on it.
 *
 * Dispatcher sibling: a new `group$kind` must add a branch here AND register its
 * meta in the island (see the CLAUDE.md dispatcher-sweep anti-pattern).
 */
export type ControlMeta =
  | { kind: "numeric"; label: string; instrument: string; default: number; format: "euro" | "percent" | "number" }
  | { kind: "delta"; label: string; instrument: string; default: number; postfix: string }
  | { kind: "bracket"; label: string; instrument: string; boundaryDefaults: number[]; rateDefaults: number[] }
  | {
      kind: "categorical";
      label: string;
      instrument: string;
      categories: Array<{ id: string; label: string; defaultBand: string }>;
      bands: Array<{ id: string; rate: number }>;
    };

export interface ControlRecord {
  value: unknown;
  isModified: boolean;
  /** Display metadata for the reform modal. Optional so the token codec and
   *  the #244 tests, which only read value/isModified, are unaffected. */
  meta?: ControlMeta;
}

export interface ShareState {
  controls: Record<string, ControlRecord>;
  recordControl: (
    inputId: string,
    value: unknown,
    isModified: boolean,
    meta?: ControlMeta,
  ) => void;
}

export const useShareStore = create<ShareState>((set) => ({
  controls: {},
  recordControl: (inputId, value, isModified, meta) =>
    set((s) => ({
      controls: { ...s.controls, [inputId]: { value, isModified, meta } },
    })),
}));

/** Imperative recorder for islands that aren't React-store consumers. */
export function recordControl(
  inputId: string,
  value: unknown,
  isModified: boolean,
  meta?: ControlMeta,
): void {
  useShareStore.getState().recordControl(inputId, value, isModified, meta);
}

/** True when at least one control differs from its default. */
export function anyModified(controls: Record<string, ControlRecord>): boolean {
  return Object.values(controls).some((c) => c.isModified);
}

/**
 * Build the shareable URL for the current modified controls:
 * `${publicBase}?s=${token}`. Only modified controls go into the token; the
 * app + engine versions ride along as the reproducibility fingerprint.
 */
export function buildShareUrl(controls: Record<string, ControlRecord>): string {
  const modified: Record<string, unknown> = {};
  for (const [key, rec] of Object.entries(controls)) {
    if (rec.isModified) modified[key] = rec.value;
  }
  const token = encodeShareToken(modified, {
    app: appVersion(),
    eng: engineVersion(),
  });
  const base = publicShareBase();
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}s=${token}`;
}
